import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@tempo-io/sui-accordion';
import { cn } from '@tempo-io/sui-class-utils';
import { KeyboardArrowDown } from '@tempo-io/sui-icons';
import { type ReactNode, useMemo, useState } from 'react';
import {
  formatCompactCurrency,
  formatCurrency,
  getCurrencySymbol,
} from '@/common/utils/formatCurrency';
import { formatPercentPoints } from '@/common/utils/formatPercentage';

const SECTION_ACCORDION_VALUE = 'content';

export const useFinancialFormat = (currencyCode: string | null) => {
  const code = currencyCode || 'USD';
  return useMemo(
    () => ({
      symbol: getCurrencySymbol(code),
      // Money amounts always round to the nearest whole unit for reporting — role rates and
      // labor hours are the deliberate exception (see `rate` below).
      money: (value: number | null | undefined) =>
        value == null
          ? '—'
          : formatCurrency(value, code, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
      compactMoney: (value: number | null | undefined) =>
        value == null ? '—' : formatCompactCurrency(value, code),
      rate: (value: number) =>
        formatCurrency(value, code, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      percent: (value: number | null | undefined) =>
        value == null ? '—' : formatPercentPoints(value),
    }),
    [code],
  );
};

// Not Radix's Accordion: it measures height once on open, clipping content that grows later.
const SectionWithHeaderValue = ({
  title,
  subtitle,
  headerValue,
  defaultOpen = true,
  children,
}: {
  title: string;
  subtitle?: string;
  headerValue: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    // shrink-0: overflow-hidden gives flex items a min-height of 0, so a tight parent could compress this instead of scrolling.
    <div className="shrink-0 overflow-hidden rounded-md border border-border-neutral">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-4 border-b border-border-neutral px-4 py-3 text-left hover:no-underline"
      >
        <div className="flex flex-col">
          <span className="text-base font-semibold text-text-neutral-primary">{title}</span>
          {subtitle && <span className="text-xs text-text-neutral-secondary">{subtitle}</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-text-brand-primary">{headerValue}</span>
          <KeyboardArrowDown
            className={cn(
              'size-5 shrink-0 text-icon-neutral-secondary transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
        </div>
      </button>
      {isOpen && (
        <div className="p-4 pt-3">
          <div className="flex flex-col gap-3">{children}</div>
        </div>
      )}
    </div>
  );
};

export const Section = ({
  title,
  subtitle,
  headerValue,
  defaultOpen = true,
  children,
}: {
  title: string;
  subtitle?: string;
  headerValue?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) => {
  if (headerValue != null) {
    return (
      <SectionWithHeaderValue
        title={title}
        subtitle={subtitle}
        headerValue={headerValue}
        defaultOpen={defaultOpen}
      >
        {children}
      </SectionWithHeaderValue>
    );
  }

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defaultOpen ? SECTION_ACCORDION_VALUE : undefined}
      className="shrink-0 overflow-hidden rounded-md border border-border-neutral"
    >
      <AccordionItem value={SECTION_ACCORDION_VALUE} className="border-b-0">
        <AccordionTrigger className="items-center gap-2 rounded-none border-b border-border-neutral bg-bg-surface-foundation px-4 py-2 hover:no-underline">
          <div className="flex flex-col text-left">
            <span className="text-xs font-semibold uppercase tracking-wide text-text-neutral-secondary">
              {title}
            </span>
            {subtitle && (
              <span className="text-xs font-normal normal-case tracking-normal text-text-neutral-secondary">
                {subtitle}
              </span>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="p-4 pt-3">
          <div className="flex flex-col gap-3">{children}</div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export const Row = ({
  label,
  sublabel,
  right,
  variant,
}: {
  label: string;
  sublabel?: string;
  right: ReactNode;
  variant?: 'total';
}) => {
  const isTotal = variant === 'total';
  const labelClassName = isTotal
    ? 'font-semibold text-text-brand-primary'
    : 'font-medium text-text-neutral-primary';
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 flex-1 flex-col">
        <span className={cn('text-sm', labelClassName)}>{label}</span>
        {sublabel && <span className="text-xs text-text-neutral-secondary">{sublabel}</span>}
      </div>
      <div
        className={cn(
          'shrink-0 text-right',
          isTotal
            ? 'text-base font-bold text-text-brand-primary'
            : 'text-sm text-text-neutral-primary',
        )}
      >
        {right}
      </div>
    </div>
  );
};

export const Divider = () => <div className="border-t border-border-neutral" />;

// A value entered at this level wins; below the lowest level, it falls back to the descendant sum.
export const computeEffectiveAmount = (
  isLowestLevel: boolean,
  selfAmount: number | null,
  descendantsAmount: number | null,
): number | null => (isLowestLevel ? selfAmount : (selfAmount ?? descendantsAmount));
